import { Link, Outlet, useLocation } from '@tanstack/react-router';
import {
  FileText,
  FlaskConical,
  GitBranch,
  LayoutDashboard,
  ListTree,
  Network,
  Sliders,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

import type { GatedCode } from '~/constants/config/feature-gates';
import { useScreenAccess } from '~/hooks/use-screen-access';
import { useFeatureGate } from '~/hooks/useFeatureGate';
import { cn } from '~/utils/cn';

import { SettingsChromeCtx } from '../context/settings-chrome-context';

/**
 * Item in the Configuration Settings inline sidebar. Mirrors the
 * `PlatformLayout` / `OrganizationLayout` shape so every control surface
 * shares the same skeleton (icon-rail icon → inline left sidebar → content).
 */
interface ConfigurationSettingsNavItem {
  /** Leaf URL the row navigates to. Use the most specific path so router
   *  doesn't have to chase index-route redirects on every click. */
  to: string;
  label: string;
  icon: LucideIcon;
  /** Path prefix used for the active-state match. Defaults to `to`; set
   *  separately when `to` is a deep child whose parent has multiple sub-pages
   *  (e.g. Configuration Manager points at `/parameters` but stays active on
   *  `/audit-trail` too via the shared `/configuration/manager` prefix). */
  activePrefix?: string;
  /** Feature-gate code from `FEATURE_GATES`. When set, the row is hidden unless the
   *  matching flag is on — the sidebar half of the page's `requireFeatureGate` guard
   *  (docs/feature-gates.md). Omit for rows that are permission-gated only. */
  /**
   * Feature-gate CODE (the FEATURE_GATES key), not the flag key. Typed to the registry so a
   * wrong string is a compile error: `flagKeyForCode` returns undefined for anything
   * unregistered and `useFeatureGate` then reads that as "ungated, always visible" — the
   * exact silent desync docs/feature-gates.md warns about.
   */
  gateCode?: GatedCode;
}

/** Hardcoded nav items. The visible list does NOT come from the API — see
 *  ScreenAccess filtering in `ConfigurationNavLink` for the access gate. */
const navItems: ConfigurationSettingsNavItem[] = [
  {
    // Skip the manager → manager/index → /parameters double-redirect cascade
    // by linking directly to the leaf; `activePrefix` keeps the row active on
    // both Parameters and Audit Trail tabs.
    to: '/configuration/manager/parameters',
    activePrefix: '/configuration/manager',
    label: 'Configuration Manager',
    icon: Sliders,
  },
  {
    to: '/configuration/treatment',
    label: 'Treatment Configuration',
    icon: FlaskConical,
  },
  {
    to: '/configuration/dashboard',
    label: 'Dashboard Configuration',
    icon: LayoutDashboard,
  },
  {
    to: '/configuration/lily-taxonomy',
    label: 'Lily Taxonomy',
    icon: GitBranch,
  },
  {
    to: '/configuration/gpc-categories',
    label: 'GPC Categories',
    icon: ListTree,
  },
  {
    to: '/configuration/fpc-categories',
    label: 'FPC Categories',
    icon: Network,
  },
  {
    to: '/configuration/prompts',
    label: 'Prompts',
    icon: FileText,
  },
];

/**
 * Inline sidebar layout for the Configuration Settings tree, mounted by
 * `/configuration/route.tsx`. Renders a hardcoded left rail with the child
 * pages (Configuration Manager, Treatment Configuration, Dashboard
 * Configuration, Lily Taxonomy, GPC Categories, FPC Categories) and an Outlet
 * for the active child.
 *
 * Pattern matches `PlatformLayout` (Platform Administration) and
 * `OrganizationLayout` (Subsidiary Settings) so every multi-page tenant
 * control surface uses the same skeleton — icon in the global rail →
 * inline left sidebar → page content on the right.
 *
 * Access:
 * - The route guards admin access via `requirePermission(Screens.CONFIGURATION)`.
 * - Each sidebar row additionally consults `useScreenAccess` so children
 *   omitted from `/screens/navigation` (admin disabled the screen, screen row
 *   missing) silently disappear instead of routing into `/access-denied`.
 *
 * Background: the content lane carries `bg-gradient-page` so the configuration
 * surface keeps the project-wide page-background convention (CLAUDE.md). The
 * sidebar lane stays on `bg-card` for visual separation.
 */
export function ConfigurationSettingsLayout() {
  // Label id ties the `<aside>` landmark to its visible heading via
  // `aria-labelledby` — keeps the heading as the single source of truth for
  // the landmark's accessible name (avoids aria-label / heading duplication).
  const labelId = useId();

  // Collapsed state lives here rather than in the page because the sidebar is the
  // page's ancestor. Pages ask for the width through `SettingsChromeCtx`; today
  // only the table browser does, while its Ask AI dock is open.
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Memoised so the context value's identity tracks the boolean and not every
  // render — consumers put `setSidebarCollapsed` in effect deps, and `useState`
  // setters are already stable, so those effects never fire spuriously.
  const chrome = useMemo(() => ({ isSidebarCollapsed, setSidebarCollapsed }), [isSidebarCollapsed]);

  return (
    // `min-h-0` on the outer + `min-w-0 min-h-0` on the content lane mirror
    // PlatformLayout: without them, tall children push the flex item past
    // viewport and leak window-level scroll on long lists.
    <div className="bg-card flex h-full min-h-0 overflow-hidden">
      {/* Collapses to zero width rather than unmounting, so it slides in step with
          the Ask AI dock opening beside it instead of the content snapping
          sideways. `inert` drops the hidden nav from tab and assistive-tech order
          — `w-0` alone would leave it keyboard-reachable. */}
      <aside
        aria-labelledby={labelId}
        className={cn(
          'shrink-0 overflow-hidden transition-[width] duration-300 ease-out',
          isSidebarCollapsed ? 'w-0' : 'w-60',
        )}
        {...(isSidebarCollapsed && { inert: true })}
      >
        {/* Fixed width on the inner lane so the nav text is clipped by the
            collapsing parent rather than reflowing as the width animates. The
            border rides on this element for the same reason: at `w-0` a border on
            the aside itself would survive as a 1px line. */}
        <div className="border-border h-full w-60 overflow-y-auto border-r py-4">
          <p
            id={labelId}
            className="text-muted-foreground mb-3 px-4 text-xs font-semibold tracking-wider uppercase"
          >
            Configuration Settings
          </p>
          <nav className="space-y-0.5 px-2">
            {navItems.map((item) => (
              <ConfigurationNavLink key={item.to} item={item} />
            ))}
          </nav>
        </div>
      </aside>

      {/* Content lane — `bg-gradient-page` restores the project-wide page
         background convention (CLAUDE.md) for every page mounted under
         /configuration/*; `min-h-full` ensures short pages still fill the
         viewport vertically. */}
      <div className="bg-gradient-page min-h-full min-w-0 flex-1 overflow-auto">
        <SettingsChromeCtx.Provider value={chrome}>
          <Outlet />
        </SettingsChromeCtx.Provider>
      </div>
    </div>
  );
}

function ConfigurationNavLink({ item }: { item: ConfigurationSettingsNavItem }) {
  const location = useLocation();
  const { isPathAllowed } = useScreenAccess();
  // Unconditional call: `useFeatureGate` answers `isAllowed: true` for any code that
  // isn't in FEATURE_GATES, including the '' placeholder an ungated row passes — so the
  // hook order stays stable without branching on `item.gateCode`.
  const { isAllowed: isFlagAllowed } = useFeatureGate(item.gateCode ?? '');

  // Hide a flag-gated row whose flag is off, matching the route's requireFeatureGate so
  // the sidebar can never offer a link that redirects to /access-denied.
  if (!isFlagAllowed) return null;

  // Hide the row when /screens/navigation doesn't list this path for the
  // current user/tenant. Mirrors the OrganizationLayout per-item permission
  // gating pattern — previously this was implicit because NavGroupPopover
  // rendered API-supplied `group.items`.
  if (!isPathAllowed(item.to)) return null;

  const prefix = item.activePrefix ?? item.to;
  // Bounded prefix match — `path` OR `path + '/'` — so a future sibling at
  // /configuration/manager-foo wouldn't accidentally light up the Manager row.
  const isActive = location.pathname === prefix || location.pathname.startsWith(prefix + '/');
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      // Preserve search params (e.g. ?platform=META) across sibling navigation.
      // Unknown keys are stripped by the destination route's validateSearch.
      search={(prev: Record<string, unknown>) => prev}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        // `min-h-11` (44px) meets WCAG 2.5.5 touch-target minimum.
        // `focus-visible:ring` gives keyboard users an unmissable focus state.
        'focus-visible:ring-ring flex min-h-11 cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        'hover:bg-accent hover:text-accent-foreground',
        isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
      )}
    >
      <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
      <span>{item.label}</span>
    </Link>
  );
}
